import { useQuery } from '@apollo/client';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { useCallback, useLayoutEffect, useRef } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ScrollView } from '@oui/app-core/src/components/ScrollView';
import { View } from '@oui/app-core/src/components/View';
import { useArtifactRequest } from '@oui/app-core/src/hooks/useArtifactResult';
import { useTheme } from '@oui/app-core/src/styles';
import { graphql } from '@oui/lib/src/graphql/tada';

import {
  WordPairingExplanation,
  WordPairingExplanationFragment,
} from '../../components/WordPairingExplanation/WordPairingExplanation';
import {
  WordPairingOverview,
  WordPairingOverviewFragment,
} from '../../components/WordPairingOverview/WordPairingOverview';
import { StackScreenProps } from '../../types/navigation';

export const WordPairingIndexQuery = graphql(
  `
    query WordPairingIndex {
      user {
        ID
        role {
          ID
          wordPairingWidget {
            ...WordPairingExplanation
            ...WordPairingOverview
            ID
            wordPairings(input: {}) {
              practiceID
              wordPairing {
                isComplete
              }
            }
          }
        }
      }
    }
  `,
  [WordPairingExplanationFragment, WordPairingOverviewFragment],
);

/**
 * Has an initial state the first time the user does the exercise and a subsequent state every time after.
 */
export const WordPairingIndex = () => {
  const { setOptions } = useNavigation<StackScreenProps<'WordPairingIndex'>['navigation']>();
  const { theme } = useTheme();
  const insets = useSafeAreaInsets();
  const { data, refetch } = useQuery(WordPairingIndexQuery);
  const initialNumCompletedRef = useRef<number>();

  const numCompleted = data?.user?.role?.wordPairingWidget?.wordPairings.filter(
    (p) => p.wordPairing.isComplete,
  ).length;

  if (typeof numCompleted === 'number' && typeof initialNumCompletedRef.current !== 'number') {
    initialNumCompletedRef.current = numCompleted;
  }

  const hasExercises = !!data?.user?.role?.wordPairingWidget?.wordPairings.length;

  // mark widget as completed if an exercise has been completed since we opened the widget
  const isComplete =
    typeof numCompleted === 'number' &&
    typeof initialNumCompletedRef.current === 'number' &&
    numCompleted > initialNumCompletedRef.current;
  useArtifactRequest('WordPairing', isComplete);

  useLayoutEffect(() => {
    setOptions({
      headerTintColor: 'white',
      headerShadowVisible: false,
      headerStyle: {
        backgroundColor: theme.color.dark,
      },
      cardStyle: { backgroundColor: theme.color.dark },
    });
  }, [setOptions, theme]);

  useFocusEffect(
    useCallback(() => {
      void refetch();
    }, [refetch]),
  );

  return (
    <ScrollView
      testID="WordPairingIndex"
      contentContainerStyle={{
        paddingBottom: insets.bottom,
      }}
      bottomOverflowColor={hasExercises ? 'white' : undefined}
    >
      <View style={{ backgroundColor: theme.color.dark, paddingHorizontal: 16, paddingBottom: 60 }}>
        <WordPairingExplanation
          patientID={data?.user?.role?.ID!}
          data={data?.user?.role?.wordPairingWidget ?? null}
          detailedExplanation={!hasExercises}
        />
      </View>
      {data?.user?.role?.wordPairingWidget && hasExercises ? (
        <View
          style={{
            marginTop: -30,
            borderTopEndRadius: 30,
            borderTopStartRadius: 30,
            backgroundColor: 'white',
          }}
        >
          <WordPairingOverview data={data.user.role.wordPairingWidget} />
        </View>
      ) : null}
    </ScrollView>
  );
};
